import { router } from "@inertiajs/react"
import { Clock, Hourglass } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { CandidateReviewCard } from "@/components/candidates/candidate-review-card"
import { EmptyState } from "@/components/empty-state"
import { HomeTriageSection } from "@/components/home-triage/home-triage-section"
import type { CandidatesToReviewSection as SectionData } from "@/types/home-triage"

/**
 * OKS-950 — Agency/Home § "En attente de la marque".
 *
 * The agency's READ-ONLY view of what it is waiting on: candidates it sent for
 * validation, with the note it wrote, how long the brand has sat on them, and
 * — past day 14 — the re-ping.
 *
 * **No Valider, no Refuser, and not because of a permission check.** The card
 * renders whatever `actions.*_url` the server supplies, and this section's
 * serializer emits literal `nil` for both. An agency does not decide its own
 * candidate at any tier, ever — that is the entire reason the brand-validation
 * step exists. See `AgencyHomeController#serialise_awaiting_brand_item` for
 * why those two keys are deliberately un-computed.
 *
 * **Named by who is waiting** — « En attente de la marque » against the
 * brand's « En attente de votre validation ». Both avoid « Candidat·e·s à … »,
 * which would have collided with the shipped « Candidat·e·s à examiner » on
 * the same page (OQ9).
 *
 * Same shell, same card, same 5-state matrix as its two siblings.
 */
export interface CandidatesAwaitingBrandSectionProps {
  section: SectionData
}

export function CandidatesAwaitingBrandSection({
  section,
}: CandidatesAwaitingBrandSectionProps) {
  const { t } = useTranslation()
  const [isRetrying, setIsRetrying] = useState(false)

  function handleRetry() {
    setIsRetrying(true)
    router.reload({
      only: ["agency_candidates_awaiting_brand"],
      onFinish: () => setIsRetrying(false),
    })
  }

  const state = isRetrying ? "loading" : section.state

  return (
    <HomeTriageSection
      title={t("candidates.awaiting_brand.title")}
      state={state}
      count={section.count}
      viewAllUrl={section.view_all_url}
      onRetry={handleRetry}
      isRetrying={isRetrying}
      testId="candidates-awaiting-brand"
      // OKS-953 (PRD R7 / UX Spec §5) — the agency home carries the SAME
      // five candidate segments, so a clean portfolio collapses to ~5 lines.
      // Collapsing only some of them would read as breakage in the others.
      collapseWhenEmpty
      emptyTestId="candidates-awaiting-brand-empty"
      renderColdStart={() => (
        <EmptyState
          icon={Hourglass}
          title={t("candidates.awaiting_brand.cold_start.title")}
          titleAs="h2"
          description={t("candidates.awaiting_brand.cold_start.description")}
          data-testid="candidates-awaiting-brand-cold-start"
        />
      )}
      renderEmpty={() => (
        <EmptyState
          icon={Clock}
          title={t("candidates.awaiting_brand.empty.title")}
          titleAs="h2"
          description={t("candidates.awaiting_brand.empty.description")}
          data-testid="candidates-awaiting-brand-empty"
        />
      )}
    >
      {section.items.map((item) => (
        <CandidateReviewCard key={item.id} item={item} />
      ))}
    </HomeTriageSection>
  )
}
